/** SUNTRONIX ONE — Approval Requests inbox.
 *  Mobile-friendly, embeddable in MY ONE / Sales Desk / Command Bar.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle2, XCircle, ArrowLeft, Clock3, RefreshCw,
  UserCog, ChevronRight,
} from "lucide-react";

const BUCKET_TONE = {
  ON_TRACK: "bg-emerald-50 text-emerald-800 border-emerald-200",
  DUE_SOON: "bg-amber-50 text-amber-800 border-amber-200",
  OVERDUE:  "bg-rose-50 text-rose-800 border-rose-200",
};


export default function MyApprovals({ compact = false }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/rules/requests/my");
      setItems(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-2" data-testid="my-approvals">
      <div className="flex items-center gap-2">
        <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-800" /> My approvals
          <span className="text-slate-400 ml-1">({items.length})</span>
        </div>
        <button onClick={load} disabled={busy}
            data-testid="my-approvals-refresh"
            className="ml-auto text-[11px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} /> Refresh
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded p-3">
          No approvals waiting on you.
        </div>
      ) : (
        <div className="space-y-1">
          {items.map((r) => (
            <div key={r.id} className="border border-slate-200 rounded-md bg-white p-2 hover:shadow-sm"
                data-testid={`approval-${r.id}`}>
              <div className="flex items-center gap-2">
                <span className={"text-[9px] px-1.5 py-0.5 rounded border font-mono " + BUCKET_TONE[r.sla_bucket]}>
                  {r.sla_bucket}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold truncate">{r.rule_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono truncate">
                    {r.module}·{r.record_id?.slice(0, 8)} · L{r.current_level}/{r.levels?.length}
                    {r.via_delegation && <span className="ml-1 text-indigo-700">· delegated</span>}
                  </div>
                </div>
                <button onClick={() => setOpenId(openId === r.id ? null : r.id)}
                    data-testid={`approval-open-${r.id}`}
                    className="text-[11px] px-3 py-2 min-h-[44px] min-w-[44px] rounded bg-slate-900 text-white">
                  Review
                </button>
              </div>
              {openId === r.id && <ApprovalCard r={r} onDone={() => { setOpenId(null); load(); }} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ApprovalCard({ r, onDone }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (decision) => {
    if ((decision === "REJECT" || decision === "RETURN") && !comment) {
      toast.error("Please add a comment for reject / return");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/rules/requests/${r.id}/decide`,
        { decision, comment });
      toast.success(`Decision recorded: ${decision}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const cur = r.levels?.find((lv) => lv.order === r.current_level);
  return (
    <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {Object.entries(r.record_summary || {}).map(([k, v]) => (
          <div key={k} className="rounded bg-slate-50 p-1.5">
            <div className="text-[9px] uppercase text-slate-500">{k.replace(/_/g, " ")}</div>
            <div className="font-semibold truncate">{String(v)}</div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-600">
        Requested by <b>{r.requested_by}</b> on <span className="font-mono">{r.requested_at?.slice(0, 19)}</span>
        {r.reason && <> · <i>{r.reason}</i></>}
      </div>
      <div className="text-[10px] text-slate-500">
        Approvers at this level: {(cur?.approvers || []).map((a) => a.role || a.email).join(", ")}
        {cur?.kind && ` · ${cur.kind}`}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)}
          rows={2} placeholder="Comment (required for reject / return)"
          data-testid={`approval-comment-${r.id}`}
          className="w-full text-[11px] border border-slate-200 rounded-md px-2 py-1" />
      <div className="flex items-center gap-1 flex-wrap">
        <button onClick={() => act("APPROVE")} disabled={busy}
            data-testid={`approval-approve-${r.id}`}
            className="text-[11px] px-3 py-1 rounded-md bg-emerald-800 text-white inline-flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" /> Approve
        </button>
        <button onClick={() => act("REJECT")} disabled={busy}
            data-testid={`approval-reject-${r.id}`}
            className="text-[11px] px-3 py-1 rounded-md border border-rose-200 text-rose-700 inline-flex items-center gap-1">
          <XCircle className="h-3 w-3" /> Reject
        </button>
        <button onClick={() => act("RETURN")} disabled={busy}
            data-testid={`approval-return-${r.id}`}
            className="text-[11px] px-3 py-1 rounded-md border border-amber-200 text-amber-800 inline-flex items-center gap-1">
          <ChevronRight className="h-3 w-3" /> Return for changes
        </button>
      </div>
    </div>
  );
}
